import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/services/api';
import { toast } from 'sonner';

const BookingModal = ({ isOpen, onClose, onSuccess, services, staff, clients, rooms, locations, onClientCreated }) => {
  const [formData, setFormData] = useState({
    client_id: '',
    service_id: '',
    staff_id: '',
    room_id: '',
    location_id: '',
    booking_datetime: '',
    payment_method: '',
    deposit_amount: 0,
    notes: '',
  });

  // Set default location when modal opens
  useEffect(() => {
    if (isOpen && locations.length > 0 && !formData.location_id) {
      setFormData(prev => ({ ...prev, location_id: locations[0].id }));
    }
  }, [isOpen, locations, formData.location_id]);

  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClient, setNewClient] = useState({
    full_name: '',
    phone: '',
    email: '',
    address: '',
    neighborhood: '',
    city: '',
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || !formData.service_id || !formData.staff_id || 
        !formData.room_id || !formData.booking_datetime) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      await api.createBooking(formData);
      onSuccess();
    } catch (error) {
      // Manejar error de disponibilidad con horarios alternativos
      if (error.response && error.response.status === 400 && error.response.data.detail) {
        const errorDetail = error.response.data.detail;
        
        if (errorDetail.error && errorDetail.alternatives) {
          // Construir mensaje con horarios alternativos
          let message = errorDetail.error;
          
          if (errorDetail.alternatives.length > 0) {
            message += '\n\nHorarios alternativos disponibles:';
            errorDetail.alternatives.forEach(altTime => {
              const dt = new Date(altTime);
              const timeStr = dt.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' });
              message += `\n• ${timeStr}`;
            });
          } else {
            message += '\n\nNo hay horarios alternativos disponibles en las próximas 2 horas.';
          }
          
          toast.error(message, {
            duration: 8000, // Mostrar por más tiempo para que puedan leer las alternativas
            style: { whiteSpace: 'pre-line' }
          });
        } else {
          toast.error(typeof errorDetail === 'string' ? errorDetail : 'Error al crear la reserva');
        }
      } else {
        toast.error('Error al crear la reserva');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.full_name || !newClient.phone) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }

    try {
      const response = await api.createClient(newClient);
      toast.success('Cliente creado');
      setFormData({ ...formData, client_id: response.data.id });
      setShowNewClientForm(false);
      setNewClient({ full_name: '', phone: '', email: '', address: '', neighborhood: '', city: '' });
      // Notify parent to reload clients
      if (onClientCreated) {
        onClientCreated(response.data);
      }
    } catch (error) {
      toast.error('Error al crear cliente');
      console.error(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-cormorant font-bold text-[#C9A961]">
            Nueva Reserva
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Client Selection */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Cliente *</Label>
            {!showNewClientForm ? (
              <div className="flex space-x-2 mt-1.5">
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger data-testid="select-client" className="flex-1">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...clients].sort((a, b) => a.full_name.localeCompare(b.full_name)).map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.full_name} - {client.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewClientForm(true)}
                  data-testid="new-client-button"
                >
                  Nuevo
                </Button>
              </div>
            ) : (
              <div className="mt-2 p-4 border border-[#C9A961]/30 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Nombre Completo *</Label>
                    <Input
                      value={newClient.full_name}
                      onChange={(e) => setNewClient({ ...newClient, full_name: e.target.value })}
                      data-testid="new-client-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Teléfono *</Label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                      data-testid="new-client-phone"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Email</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Ciudad</Label>
                    <Input
                      value={newClient.city}
                      onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Dirección</Label>
                    <Input
                      value={newClient.address}
                      onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Barrio</Label>
                    <Input
                      value={newClient.neighborhood}
                      onChange={(e) => setNewClient({ ...newClient, neighborhood: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button type="button" onClick={handleCreateClient} size="sm" className="bg-[#C9A961]">
                    Crear Cliente
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewClientForm(false)}
                    size="sm"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Service */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Servicio *</Label>
            <Select
              value={formData.service_id}
              onValueChange={(value) => setFormData({ ...formData, service_id: value })}
            >
              <SelectTrigger data-testid="select-service" className="mt-1.5">
                <SelectValue placeholder="Seleccionar servicio" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name} ({service.duration_minutes} min)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Staff */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Asignado a *</Label>
            <Select
              value={formData.staff_id}
              onValueChange={(value) => setFormData({ ...formData, staff_id: value })}
            >
              <SelectTrigger data-testid="select-staff" className="mt-1.5">
                <SelectValue placeholder="Seleccionar operaria" />
              </SelectTrigger>
              <SelectContent>
                {staff.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.name} - {member.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Room */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Sala *</Label>
            <Select
              value={formData.room_id}
              onValueChange={(value) => setFormData({ ...formData, room_id: value })}
            >
              <SelectTrigger data-testid="select-room" className="mt-1.5">
                <SelectValue placeholder="Seleccionar sala" />
              </SelectTrigger>
              <SelectContent>
                {rooms.map((room) => (
                  <SelectItem key={room.id} value={room.id}>
                    {room.name} (Capacidad: {room.capacity})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date & Time */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Fecha y Hora *</Label>
            <Input
              type="datetime-local"
              value={formData.booking_datetime}
              onChange={(e) => setFormData({ ...formData, booking_datetime: e.target.value })}
              data-testid="booking-datetime-input"
              className="mt-1.5"
            />
          </div>

          {/* Payment Method */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Método de Pago</Label>
            <Select
              value={formData.payment_method}
              onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
            >
              <SelectTrigger data-testid="select-payment-method" className="mt-1.5">
                <SelectValue placeholder="Seleccionar método" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Efectivo</SelectItem>
                <SelectItem value="transfer">Transferencia</SelectItem>
                <SelectItem value="mercadopago">Mercado Pago</SelectItem>
                <SelectItem value="card">Tarjeta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Deposit Amount */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Monto Seña/Pago ($)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.deposit_amount}
              onChange={(e) => setFormData({ ...formData, deposit_amount: parseFloat(e.target.value) || 0 })}
              data-testid="deposit-amount-input"
              className="mt-1.5"
            />
          </div>

          {/* Notes */}
          <div>
            <Label className="text-[#2C2C2C] font-medium">Notas</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              data-testid="booking-notes-textarea"
              className="mt-1.5"
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="submit-booking-button"
              disabled={loading}
              className="bg-[#C9A961] hover:bg-[#B89551]"
            >
              {loading ? 'Creando...' : 'Crear Reserva'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;